import React from 'react';
import { Card, Button, Typography, Spin, Space, Avatar } from 'antd';
import { GoogleOutlined, LoadingOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;

const AuthGuard = ({ children }) => {
  const { user, loading, signInWithGoogle, signOut, getUserName, getUserPhoto } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #FFF8F0 0%, #FFFFFF 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕉️</div>
          <Spin 
            size="large" 
            indicator={<LoadingOutlined style={{ fontSize: 32, color: '#FF8C00' }} spin />}
          />
          <div style={{ color: '#FF8C00', fontSize: '18px', marginTop: '16px' }}>
            Loading your spiritual journey...
          </div>
        </div>
      </div>
    );
  }

  // Show login screen if user is not authenticated
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #FFF8F0 0%, #FFFFFF 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Card 
          style={{ 
            maxWidth: '500px', 
            width: '100%',
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(255, 140, 0, 0.15)',
            border: '2px solid #FFE4B5'
          }}
          styles={{ body: { padding: '40px' } }}
        >
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '80px', marginBottom: '16px' }}>🕉️</div>
            <Title level={2} style={{ color: '#FF8C00', margin: 0 }}>
              ProgressCraft Parayana
            </Title>
            <Text style={{ color: '#FF7F00', fontSize: '16px' }}>
              Your Digital Spiritual Journey
            </Text>
          </div>

          <Card 
            size="small"
            style={{ 
              backgroundColor: '#FFF9F0',
              border: '1px solid #FFD591',
              marginBottom: '24px'
            }}
          >
            <Paragraph style={{ margin: 0, color: '#D48806', fontSize: '14px' }}>
              <strong>Welcome to your spiritual practice tracker!</strong>
              <br />
              • Track your parayana counts and progress
              <br />
              • View detailed analytics of your spiritual journey
              <br />
              • Sync across all your devices securely
            </Paragraph>
          </Card>

          <Button
            type="primary"
            size="large"
            icon={<GoogleOutlined />}
            onClick={signInWithGoogle}
            loading={loading}
            block
            style={{
              height: '56px',
              fontSize: '18px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(255, 140, 0, 0.3)'
            }}
          >
            Continue with Google
          </Button>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Text style={{ color: '#999', fontSize: '12px' }}>
              By signing in, you agree to our terms of service.<br />
              Your spiritual data is kept private and secure.
            </Text>
          </div>

          <div style={{ 
            textAlign: 'center', 
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#F0F7FF',
            borderRadius: '8px'
          }}>
            <Text style={{ color: '#1890FF', fontSize: '13px' }}>
              🔒 <strong>Secure Authentication</strong> - Powered by Google & Firebase
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  // Show app header with user info and logout for authenticated users
  return (
    <div>
      {/* Auth Header */}
      <div style={{
        background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE4B5 100%)',
        borderBottom: '1px solid #FFB347',
        padding: '8px 16px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar 
              src={getUserPhoto()} 
              size={32}
              style={{ backgroundColor: '#FF8C00' }}
            >
              {getUserName().charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <Text style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#FF8C00',
                display: 'block',
                lineHeight: '1.2'
              }}>
                {getUserName()}
              </Text>
              <Text style={{ 
                fontSize: '11px', 
                color: '#D48806',
                lineHeight: '1.2'
              }}>
                {user.email}
              </Text>
            </div>
          </div>
          
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={signOut}
            size="small"
            style={{ 
              color: '#FF8C00',
              fontSize: '12px'
            }}
          >
            Sign Out
          </Button>
        </div>
      </div>

      {/* App Content */}
      <div style={{ minHeight: 'calc(100vh - 60px)' }}>
        {children}
      </div>
    </div>
  );
};

export default AuthGuard; 